import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function ContactSection() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d4dv5j9p7jt0gmqepgc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus(t('contact_form_success'));
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
      } else {
        setSubmitStatus(t('contact_form_error'));
      }
    } catch (error) {
      setSubmitStatus(t('contact_form_error'));
    }
    
    setIsSubmitting(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t('contact_title')}<span className="text-blue-600">{t('contact_title_highlight')}</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                {t('contact_intro')}
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contact_address_title')}</h3>
                  <p className="text-gray-600">{t('contact_address_value')}</p>
                  <p className="text-gray-500 text-sm mt-1">{t('contact_address_value_en')}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contact_phone_title')}</h3>
                  <a href={`tel:${t('contact_phone_value')}`} className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                    {t('contact_phone_value')}
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contact_email_title')}</h3>
                  <a href={`mailto:${t('contact_email_value')}`} className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                    {t('contact_email_value')}
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-global-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contact_coverage_title')}</h3>
                  <p className="text-gray-600">{t('contact_coverage_value')}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{t('contact_hours_title')}</h3>
                  <p className="text-gray-600">{t('contact_hours_value')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('contact_form_name')} *</label>
                <input
                  type="text"
                  name="name"
                  placeholder={t('contact_form_name_placeholder')}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('contact_form_email')} *</label>
                <input
                  type="email"
                  name="email"
                  placeholder={t('contact_form_email_placeholder')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('contact_form_phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('contact_form_phone_placeholder')}
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('contact_form_company')}</label>
                <input
                  type="text"
                  name="company"
                  placeholder={t('contact_form_company_placeholder')}
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">{t('contact_form_message')} *</label>
                <textarea
                  name="message"
                  placeholder={t('contact_form_message_placeholder')}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 resize-none"
                ></textarea>
                <div className="text-right text-gray-500 text-sm mt-2">
                  {formData.message.length}{t('contact_form_char_limit')}
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-medium hover:bg-blue-700 transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? t('contact_form_submitting') : t('contact_form_submit')}
              </button>
              
              {submitStatus && (
                <div className={`text-center py-2 rounded-lg ${submitStatus.includes(t('contact_form_success').substring(0, 4)) ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                  {submitStatus}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
